import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
  Chip,
  Collapse,
  Table,
  TableContainer,
  Paper,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { fetchWithToken } from "../../../api/apiutils";

// Коды статусов по блокам анализа (см. постановку) — те же, что в BinActiveUserPage
const ANALYSIS_STATUS = {
  PENDING: 1,
  ANALYZED: 2,
  CANT_ANALYZE: 3,
  HAVENT_ANALYZED: 4,
  IN_PROGRESS: 5,
  BANNED: 6,
  NOT_FOUND: 7,
  UNAVAILABLE: 8,
};

const STATUS_LABELS = {
  [ANALYSIS_STATUS.PENDING]: "В очереди",
  [ANALYSIS_STATUS.ANALYZED]: "Готово",
  [ANALYSIS_STATUS.CANT_ANALYZE]: "Не удалось проанализировать",
  [ANALYSIS_STATUS.HAVENT_ANALYZED]: "Данных нет",
  [ANALYSIS_STATUS.IN_PROGRESS]: "Считается",
  [ANALYSIS_STATUS.BANNED]: "Заблокировано",
  [ANALYSIS_STATUS.NOT_FOUND]: "Не найдено",
  [ANALYSIS_STATUS.UNAVAILABLE]: "Недоступно",
};

const POLL_INTERVAL_MS = 5000;

// Единый стиль карточек — тень + скругление, чтобы границы читались визуально
const cardSx = {
  borderRadius: 3,
  boxShadow: "0 10px 28px rgba(0,0,0,0.16), 0 3px 8px rgba(0,0,0,0.10)",
  border: "1px solid rgba(0,0,0,0.06)",
};

const getValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value?.length === 0
  ) {
    return "Не указано";
  }
  return value;
};

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const KTRU_PAGE_LIMIT = 20;
const KTRU_POLL_INTERVAL_MS = 5000;

/**
 * Хук для витрин КТРУ-статистики компании (заявки/победы и контракты).
 * Обе ручки — только чтение, ничего не пересчитывают, поэтому дергаем их
 * лениво (enabled=true только когда карточка раскрыта) и с пагинацией.
 *
 * Особенности по постановке:
 * - 400 AnlysisPending — раздел ещё считается, не ошибка, нужно поллить именно
 *   эту ручку дальше (вторую ручку это не блокирует).
 * - 409 AnalysisNotAvailable — раздел сломался, поллить бесполезно, нужно
 *   показать сообщение и предложить перезапустить анализ.
 * - 404 — анализ не найден/чужой/БИН не совпал, повторять не нужно.
 * - ktruName может быть null (кода нет в справочнике) — строку не выкидываем,
 *   показываем прочерк.
 */
function useKtruStatistics({ enabled, endpoint, analysisId, bin }) {
  const [state, setState] = useState({
    loading: false,
    pending: false,
    unavailable: false,
    error: null,
    result: null,
    page: 1,
  });

  const pollRef = useRef(null);
  const stopPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchPage = async (page) => {
    if (!analysisId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetchWithToken(
        `${endpoint}?AnalysisId=${analysisId}&Identifier=${bin}&Page=${page}&PageLimit=${KTRU_PAGE_LIMIT}`,
      );

      if (res.status === 400) {
        const body = await res.json().catch(() => null);
        if (body?.code === "AnlysisPending") {
          // не ошибка — раздел ещё считается, продолжаем опрос именно этой ручки
          setState((s) => ({
            ...s,
            loading: false,
            pending: true,
            unavailable: false,
            error: null,
          }));
          if (!pollRef.current) {
            pollRef.current = setInterval(
              () => fetchPage(page),
              KTRU_POLL_INTERVAL_MS,
            );
          }
          return;
        }
        stopPoll();
        setState((s) => ({
          ...s,
          loading: false,
          pending: false,
          error: "Некорректный запрос",
        }));
        return;
      }

      if (res.status === 409) {
        stopPoll();
        setState((s) => ({
          ...s,
          loading: false,
          pending: false,
          unavailable: true,
        }));
        return;
      }

      if (res.status === 404) {
        stopPoll();
        setState((s) => ({
          ...s,
          loading: false,
          pending: false,
          error: "Анализ не найден",
        }));
        return;
      }

      if (!res.ok) {
        stopPoll();
        setState((s) => ({
          ...s,
          loading: false,
          error: "Не удалось загрузить данные",
        }));
        return;
      }

      const json = await res.json();
      stopPoll();
      setState({
        loading: false,
        pending: false,
        unavailable: false,
        error: null,
        result: json,
        page,
      });
    } catch (e) {
      console.error("Ошибка загрузки КТРУ-статистики:", e);
      setState((s) => ({
        ...s,
        loading: false,
        error: "Не удалось загрузить данные",
      }));
    }
  };

  useEffect(() => {
    if (!enabled || !analysisId) return;
    fetchPage(1);
    return () => stopPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, analysisId, bin, endpoint]);

  const goToPage = (page) => {
    stopPoll();
    fetchPage(page);
  };

  return { ...state, goToPage };
}

/**
 * Пагинация для витрин КТРУ. "all" в ответе — это количество строк во
 * всём рейтинге (а не сумма заявок/контрактов), от него и считаем
 * количество страниц.
 */
function KtruPagination({ stats }) {
  const totalRows = stats.result?.all ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / KTRU_PAGE_LIMIT));
  const page = stats.page || 1;

  if (totalPages <= 1) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        mt: 1,
      }}
    >
      <Button
        size="small"
        disabled={page <= 1 || stats.loading}
        onClick={() => stats.goToPage(page - 1)}
      >
        Назад
      </Button>
      <Typography variant="body2" color="text.secondary">
        Страница {page} из {totalPages}
      </Typography>
      <Button
        size="small"
        disabled={page >= totalPages || stats.loading}
        onClick={() => stats.goToPage(page + 1)}
      >
        Далее
      </Button>
    </Box>
  );
}

/**
 * Карточка компании по БИН/ИИН.
 * Повторяет логику страницы BinActiveUserPage (поиск/создание анализа,
 * поллинг статуса, подгрузка блоков), но встраивается инлайн (например,
 * в паспорт пользователя) вместо перехода на отдельную страницу.
 *
 * По умолчанию карточка компактная и рассчитана на отображение в ряд
 * вместе с другими БИН-карточками. Полная информация раскрывается по
 * кнопке «Смотреть подробнее».
 */
const BinCompanyDetailsCard = ({ bin }) => {
  const [analysisId, setAnalysisId] = useState(null);
  const [analysisMeta, setAnalysisMeta] = useState(null);
  const [isAnalysing, setIsAnalysing] = useState(true);
  const [noData, setNoData] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const [data, setData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [applicationsByYear, setApplicationsByYear] = useState({});
  const [contractsSummary, setContractsSummary] = useState(null);
  const [contractsByYear, setContractsByYear] = useState({});
  const [taxes, setTaxes] = useState({});

  const pollTimerRef = useRef(null);
  const loadedRef = useRef(false); // чтобы не грузить блоки повторно при каждом опросе
  const creatingRef = useRef(false); // защита от повторного создания анализа

  // --- КТРУ-статистика: рейтинг по заявкам/победам и по контрактам ---
  // Тянем только когда карточка раскрыта и общий анализ уже готов (isReady).
  const ktruEnabled =
    expanded && !isAnalysing && !noData && Boolean(analysisId);
  const ktruApplicationsStats = useKtruStatistics({
    enabled: ktruEnabled,
    endpoint: "/api/companies/applications/ktru-statistics",
    analysisId,
    bin,
  });
  const ktruContractsStats = useKtruStatistics({
    enabled: ktruEnabled,
    endpoint: "/api/companies/contracts/ktru-statistics",
    analysisId,
    bin,
  });

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const startPolling = () => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(checkAnalysis, POLL_INTERVAL_MS);
  };

  // --- загрузка отдельных блоков карточки, каждый только если его статус Analyzed(2) ---
  const loadCompanyData = async (meta, id) => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    if (meta.detailInformationStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const companies = await fetchWithToken(
          `/api/companies?AnalysisId=${id}&Identifier=${bin}`,
        );
        setData(await companies.json());
      } catch (error) {
        console.error("Ошибка при загрузке карточки компании:", error);
      }
    }

    if (meta.applicationsSummaryStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const summaryResponse = await fetchWithToken(
          `/api/companies/applications/summary?AnalysisId=${id}&Identifier=${bin}`,
        );
        setSummaryData(await summaryResponse.json());
      } catch (error) {
        console.error("Ошибка при загрузке summary:", error);
      }
    }

    if (meta.contractsSummaryStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const contractsSummaryResponse = await fetchWithToken(
          `/api/companies/contracts/summary?AnalysisId=${id}&Identifier=${bin}`,
        );
        setContractsSummary(await contractsSummaryResponse.json());
      } catch (error) {
        console.error("Ошибка при загрузке contracts summary:", error);
      }
    }

    if (meta.taxesStatus === ANALYSIS_STATUS.ANALYZED) {
      try {
        const taxesResponse = await fetchWithToken(
          `/api/companies/taxes?AnalysisId=${id}&Identifier=${bin}`,
        );
        setTaxes(await taxesResponse.json());
      } catch (error) {
        console.error("Ошибка при загрузке налогов:", error);
      }
    }
  };

  // --- создание нового анализа, если по БИН ещё ничего нет ---
  const createAnalysis = async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    try {
      const response = await fetchWithToken("/api/companies/analyses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: bin, type: "OneTime" }),
      });
      if (!response.ok) throw new Error(response.statusText);
      await response.json();
      setIsAnalysing(true);
      setNoData(false);
      startPolling();
    } catch (error) {
      console.error("Ошибка при создании анализа:", error);
      setIsAnalysing(false);
      setNoData(true);
    } finally {
      creatingRef.current = false;
    }
  };

  // --- опрос GET /api/companies/analyses по БИН, пока анализ не готов ---
  const checkAnalysis = async () => {
    try {
      const res = await fetchWithToken(
        `/api/companies/analyses?Identifier=${bin}&Page=1&PageLimit=1`,
      );
      const result = await res.json();
      const analysis = result?.data?.[0];

      if (!analysis) {
        // анализа по этому БИН ещё нет — создаём и продолжаем опрос
        await createAnalysis();
        return;
      }

      setAnalysisId(analysis.id);
      setAnalysisMeta(analysis);

      if (analysis.isReady) {
        setIsAnalysing(false);
        setNoData(false);
        stopPolling();
        loadCompanyData(analysis, analysis.id);
      } else if (analysis.isProcessing) {
        setIsAnalysing(true);
        setNoData(false);
        startPolling();
      } else {
        // всё отработало, но детальной информации по компании нет
        setIsAnalysing(false);
        setNoData(true);
        stopPolling();
      }
    } catch (error) {
      console.error("Ошибка при проверке статуса анализа:", error);
    }
  };

  const handleRestartAnalysis = async () => {
    setRestarting(true);
    try {
      const response = await fetchWithToken("/api/companies/analyses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: bin, type: "OneTime" }),
      });
      if (!response.ok) throw new Error(response.statusText);
      loadedRef.current = false;
      setNoData(false);
      await checkAnalysis();
    } catch (error) {
      console.error("Ошибка при перезапуске анализа:", error);
    } finally {
      setRestarting(false);
    }
  };

  // --- загрузка заявок (по клику на КТРУ/метод закупки) ---
  const loadApplications = async ({ year, truCode, tradeMethodId }) => {
    if (analysisMeta?.applicationsStatus !== ANALYSIS_STATUS.ANALYZED) return;

    let url = `/api/companies/applications?AnalysisId=${analysisId}&Identifier=${bin}`;
    if (year) url += `&Year=${year}`;
    if (truCode) url += `&TruCode=${truCode}`;
    if (tradeMethodId) url += `&TradeMethodId=${tradeMethodId}`;

    try {
      const applicationsResponse = await fetchWithToken(url);
      const applicationData = await applicationsResponse.json();

      setApplicationsByYear((prev) => ({
        ...prev,
        [year]: applicationData.data || [],
      }));
    } catch (error) {
      console.error("Ошибка при загрузке applications:", error);
    }
  };

  // --- загрузка контрактов (по клику на КТРУ/метод закупки) ---
  const loadContracts = async ({ year, truCode, tradeMethodId }) => {
    if (analysisMeta?.contractsStatus !== ANALYSIS_STATUS.ANALYZED) return;

    let url = `/api/companies/contracts?AnalysisId=${analysisId}&Identifier=${bin}`;
    if (year) url += `&Year=${year}`;
    if (truCode) url += `&TruCode=${truCode}`;
    if (tradeMethodId) url += `&TradeMethodId=${tradeMethodId}`;

    try {
      const contractsResponse = await fetchWithToken(url);
      const contractsData = await contractsResponse.json();

      setContractsByYear((prev) => ({
        ...prev,
        [year]: contractsData || [],
      }));
    } catch (error) {
      console.error("Ошибка при загрузке contracts:", error);
    }
  };

  useEffect(() => {
    // сброс состояния при смене БИН
    loadedRef.current = false;
    setAnalysisId(null);
    setAnalysisMeta(null);
    setData(null);
    setSummaryData(null);
    setContractsSummary(null);
    setApplicationsByYear({});
    setContractsByYear({});
    setTaxes({});
    setNoData(false);
    setIsAnalysing(true);
    setExpanded(false);

    checkAnalysis();

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bin]);

  const statusChip = () => {
    if (isAnalysing) {
      return <Chip size="small" color="warning" label="Анализ..." />;
    }
    if (noData) {
      return <Chip size="small" color="default" label="Нет данных" />;
    }
    return <Chip size="small" color="success" label="Готово" />;
  };

  return (
    // flex-элемент: в свёрнутом виде — компактная колонка в ряду,
    // в раскрытом — растягивается на всю ширину ряда
    <Box
      sx={{
        flex: expanded ? "1 1 100%" : "1 1 280px",
        maxWidth: expanded ? "100%" : 340,
        minWidth: 260,
        transition: "max-width 0.2s ease",
      }}
    >
      <Card
        sx={{
          ...cardSx,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardHeader
          title={`БИН/ИИН: ${bin}`}
          subheader={data?.name ? getValue(data.name) : null}
          titleTypographyProps={{ fontSize: 15, fontWeight: 600 }}
          subheaderTypographyProps={{
            fontSize: 13,
            noWrap: !expanded,
            sx: !expanded
              ? { overflow: "hidden", textOverflow: "ellipsis" }
              : undefined,
          }}
          action={statusChip()}
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0, flexGrow: 1 }}>
          <Divider sx={{ mb: 1.5 }} />

          {isAnalysing && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
            >
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Идёт анализ, страница обновится автоматически
              </Typography>
            </Box>
          )}

          {noData && (
            <Box sx={{ py: 1 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1.5 }}
                color="text.secondary"
              >
                Детальная информация не найдена
                {analysisMeta &&
                  ` (статус: ${STATUS_LABELS[analysisMeta.detailInformationStatus] || "неизвестно"})`}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={restarting}
                onClick={handleRestartAnalysis}
              >
                {restarting ? (
                  <CircularProgress size={16} />
                ) : (
                  "Перезапустить анализ"
                )}
              </Button>
            </Box>
          )}

          {!isAnalysing && !noData && (
            <>
              {/* --- компактная сводка, видна всегда --- */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                {summaryData && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Заявок: ${getValue(summaryData.all)}`}
                  />
                )}
                {contractsSummary && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Контрактов: ${getValue(contractsSummary.count)}`}
                  />
                )}
                {data && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={data.supplier ? "Поставщик" : "Не поставщик"}
                  />
                )}
              </Box>

              <Button
                size="small"
                fullWidth
                variant="outlined"
                onClick={() => setExpanded((prev) => !prev)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {expanded ? "Скрыть" : "Смотреть подробнее"}
              </Button>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ display: "grid", gap: 3, mt: 2.5 }}>
                  {/* --- карточка компании --- */}
                  {data ? (
                    <Box>
                      <Divider sx={{ mb: 1 }}>
                        <strong>Общая информация</strong>
                      </Divider>
                      <Box sx={{ display: "grid", gap: 1 }}>
                        <Typography>
                          <strong>БИН/ИИН:</strong> {getValue(data.identifier)}
                        </Typography>
                        <Typography>
                          <strong>Дата регистрации:</strong>{" "}
                          {getValue(data.registrationDate)}
                        </Typography>
                        <Typography>
                          <strong>Наименование организации:</strong>{" "}
                          {getValue(data.name)}
                        </Typography>
                        <Typography>
                          <strong>Электронная почта:</strong>{" "}
                          {getValue(data.contact?.email)}
                        </Typography>
                        <Typography>
                          <strong>Веб-сайт:</strong>{" "}
                          {getValue(data.contact?.website)}
                        </Typography>
                        <Typography>
                          <strong>Телефоны:</strong>{" "}
                          {Array.isArray(data.contact?.phoneNumbers)
                            ? data.contact.phoneNumbers.join(", ")
                            : getValue(data.contact?.phoneNumbers)}
                        </Typography>
                        <Typography>
                          <strong>Тип организации:</strong>{" "}
                          {getValue(data.type)}
                        </Typography>
                        <Typography component="div">
                          <strong>ОКЭД (Вид деятельности):</strong>{" "}
                          {Array.isArray(data.okeds) ? (
                            <ul>
                              {data.okeds.map((o, idx) => (
                                <li key={idx}>
                                  {o.code} - {o.name}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            getValue(data.okeds?.name)
                          )}
                        </Typography>
                        <Typography>
                          <strong>Адреса:</strong>{" "}
                          {Array.isArray(data.addresses)
                            ? data.addresses.join(", ")
                            : getValue(data.addresses)}
                        </Typography>
                        <Typography>
                          <strong>Поставщик:</strong>{" "}
                          {data.supplier ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Заказчик:</strong>{" "}
                          {data.customer ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Организатор:</strong>{" "}
                          {data.organizer ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Нац. компания:</strong>{" "}
                          {data.isNationalCompany ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Организация инвалидов:</strong>{" "}
                          {data.isAssocWithDisab ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Малое предприятие:</strong>{" "}
                          {data.isSmallEmployer ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Гос. монополия:</strong>{" "}
                          {data.isStateMonopoly ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Патронажный поставщик:</strong>{" "}
                          {data.isPatronymicSupplyer ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Резидент:</strong>{" "}
                          {data.isResident ? "Да" : "Нет"}
                        </Typography>
                        <Typography>
                          <strong>Квазигос. сектор:</strong>{" "}
                          {data.qvazi ? "Да" : "Нет"}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      Детальная информация по компании недоступна
                      {analysisMeta &&
                        ` (статус: ${STATUS_LABELS[analysisMeta.detailInformationStatus] || "неизвестно"})`}
                    </Typography>
                  )}

                  {/* --- заявки --- */}
                  <Box>
                    <Divider sx={{ mb: 1 }}>
                      <strong>Заявки по годам</strong>
                    </Divider>
                    {summaryData ? (
                      <>
                        <Typography sx={{ mb: 1 }}>
                          <strong>Всего заявок:</strong>{" "}
                          {getValue(summaryData.all)}
                        </Typography>
                        <Typography sx={{ mb: 1 }}>
                          <strong>Количество побед:</strong>{" "}
                          {getValue(summaryData.numberOfWinnings)}
                        </Typography>

                        {summaryData.byYear?.map((yearItem, idx) => (
                          <Accordion key={idx} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>
                                {yearItem.year} — всего:{" "}
                                {getValue(yearItem.all)}, побед:{" "}
                                {getValue(yearItem.numberOfWinnings)}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ display: "grid", gap: 2 }}>
                                <Divider>
                                  <strong>по КТРУ</strong>
                                </Divider>
                                {yearItem.applications?.byTru?.map((tru, i) => (
                                  <Box
                                    key={i}
                                    sx={{ ml: 2, cursor: "pointer" }}
                                    onClick={() =>
                                      loadApplications({
                                        year: yearItem.year,
                                        truCode: tru.item?.code,
                                      })
                                    }
                                  >
                                    <Typography>
                                      {getValue(tru.item?.name)} (
                                      {getValue(tru.item?.code)})
                                    </Typography>
                                    <Typography variant="body2">
                                      Всего: {getValue(tru.stats?.all)}, Побед:{" "}
                                      {getValue(tru.stats?.numberOfWinnings)}
                                    </Typography>
                                  </Box>
                                ))}

                                <Divider>
                                  <strong>По методам закупок</strong>
                                </Divider>
                                {yearItem.applications?.byTradeMethod?.map(
                                  (method, i) => (
                                    <Box
                                      key={i}
                                      sx={{ ml: 2, cursor: "pointer" }}
                                      onClick={() =>
                                        loadApplications({
                                          year: yearItem.year,
                                          tradeMethodId: method.item?.code,
                                        })
                                      }
                                    >
                                      <Typography>
                                        {getValue(method.item?.name)} (
                                        {getValue(method.item?.code)})
                                      </Typography>
                                      <Typography variant="body2">
                                        Всего: {getValue(method.stats?.all)},
                                        Побед:{" "}
                                        {getValue(
                                          method.stats?.numberOfWinnings,
                                        )}
                                      </Typography>
                                    </Box>
                                  ),
                                )}
                              </Box>

                              {analysisMeta?.applicationsStatus !==
                                ANALYSIS_STATUS.ANALYZED && (
                                <Typography
                                  sx={{ mt: 2 }}
                                  color="text.secondary"
                                >
                                  Список заявок недоступен (статус:{" "}
                                  {STATUS_LABELS[
                                    analysisMeta?.applicationsStatus
                                  ] || "неизвестно"}
                                  )
                                </Typography>
                              )}
                              {applicationsByYear[yearItem.year]?.length >
                                0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Divider>Список заявок</Divider>
                                  {applicationsByYear[yearItem.year].map(
                                    (app, idx2) => (
                                      <Box
                                        key={idx2}
                                        sx={{
                                          border: "1px solid #ddd",
                                          borderRadius: "8px",
                                          p: 1,
                                          my: 1,
                                        }}
                                      >
                                        <Typography>
                                          <strong>ID:</strong>{" "}
                                          {app.applicationId}
                                        </Typography>
                                        <Typography>
                                          <strong>Статус:</strong>{" "}
                                          {getValue(app.status)}
                                        </Typography>
                                        <Typography>
                                          <strong>Заказчик:</strong>{" "}
                                          {app.customer?.name || "Не указано"}
                                        </Typography>
                                        <Typography>
                                          <strong>План. сумма:</strong>{" "}
                                          {getValue(app.plannedAmount)}
                                        </Typography>
                                        <Typography>
                                          <strong>Предложенная сумма:</strong>{" "}
                                          {getValue(app.amount)}
                                        </Typography>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </>
                    ) : (
                      <Typography color="text.secondary">
                        Сводка по заявкам недоступна
                        {analysisMeta &&
                          ` (статус: ${STATUS_LABELS[analysisMeta.applicationsSummaryStatus] || "неизвестно"})`}
                      </Typography>
                    )}
                  </Box>

                  {/* --- Рейтинг КТРУ по заявкам и победам ---
                      GET /api/companies/applications/ktru-statistics.
                      Только чтение, ничего не пересчитывает; тянем лениво,
                      как только карточка раскрыта и анализ готов. */}
                  <Box>
                    <Divider sx={{ mb: 1 }}>
                      <strong>Рейтинг КТРУ по заявкам и победам</strong>
                    </Divider>
                    {ktruApplicationsStats.loading &&
                    !ktruApplicationsStats.result ? (
                      <CircularProgress size={20} />
                    ) : ktruApplicationsStats.pending ? (
                      <Typography color="text.secondary">
                        Раздел ещё считается, данные появятся автоматически
                      </Typography>
                    ) : ktruApplicationsStats.unavailable ? (
                      <Box>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          Раздел анализа завершился с ошибкой
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={restarting}
                          onClick={handleRestartAnalysis}
                        >
                          {restarting ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Перезапустить анализ"
                          )}
                        </Button>
                      </Box>
                    ) : ktruApplicationsStats.error ? (
                      <Typography color="error">
                        {ktruApplicationsStats.error}
                      </Typography>
                    ) : ktruApplicationsStats.result ? (
                      <>
                        {ktruApplicationsStats.result
                          .unmappedApplicationsCount > 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Заявок без указанного кода КТРУ:{" "}
                            {
                              ktruApplicationsStats.result
                                .unmappedApplicationsCount
                            }
                            {ktruApplicationsStats.result
                              .unmappedWinningsCount > 0 &&
                              `, из них побед: ${ktruApplicationsStats.result.unmappedWinningsCount}`}
                          </Typography>
                        )}
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          sx={{ boxShadow: "0 6px 16px rgba(0,0,0,0.10)" }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Код КТРУ</TableCell>
                                <TableCell>Название</TableCell>
                                <TableCell>Заявок</TableCell>
                                <TableCell>Побед</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ktruApplicationsStats.result.data?.length ? (
                                ktruApplicationsStats.result.data.map(
                                  (row, idx) => (
                                    <TableRow key={`${row.ktruCode}-${idx}`}>
                                      <TableCell>{row.ktruCode}</TableCell>
                                      <TableCell>
                                        {row.ktruName || "—"}
                                      </TableCell>
                                      <TableCell>
                                        {row.applicationsCount}
                                      </TableCell>
                                      <TableCell>{row.winningsCount}</TableCell>
                                    </TableRow>
                                  ),
                                )
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    Нет данных
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <KtruPagination stats={ktruApplicationsStats} />
                      </>
                    ) : null}
                  </Box>

                  {/* --- контракты --- */}
                  <Box>
                    <Divider sx={{ mb: 1 }}>
                      <strong>Контракты по годам</strong>
                    </Divider>
                    {contractsSummary ? (
                      <>
                        <Typography sx={{ mb: 1 }}>
                          <strong>Всего контрактов:</strong>{" "}
                          {getValue(contractsSummary.count)}
                        </Typography>
                        <Typography sx={{ mb: 1 }}>
                          <strong>Общая сумма (без НДС)</strong>{" "}
                          {getValue(contractsSummary.totalAmount)}
                        </Typography>
                        <Typography sx={{ mb: 1 }}>
                          <strong>Общая сумма (с НДС)</strong>{" "}
                          {getValue(contractsSummary.totalAmountWithNds)}
                        </Typography>

                        {contractsSummary.byYear?.map((yearItem, idx) => (
                          <Accordion key={idx} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>
                                {yearItem.year} — всего:{" "}
                                {getValue(yearItem.count)}, Сумма в году (без
                                НДС): {getValue(yearItem.totalAmount)}, Сумма в
                                году (с НДС):{" "}
                                {getValue(contractsSummary.totalAmountWithNds)}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ display: "grid", gap: 2 }}>
                                <Divider>
                                  <strong>по КТРУ</strong>
                                </Divider>
                                {yearItem.contracts?.byTru?.map((tru, i) => (
                                  <Box
                                    key={i}
                                    sx={{ ml: 2, cursor: "pointer" }}
                                    onClick={() =>
                                      loadContracts({
                                        year: yearItem.year,
                                        truCode: tru.item?.code,
                                      })
                                    }
                                  >
                                    <Typography>
                                      {getValue(tru.item?.name)} (
                                      {getValue(tru.item?.code)})
                                    </Typography>
                                    <Typography variant="body2">
                                      Всего: {getValue(tru.count)}, Сумма в году
                                      (без НДС): {getValue(tru.totalAmount)},
                                      Сумма в году (с НДС):{" "}
                                      {getValue(tru.totalAmountWithNds)}
                                    </Typography>
                                  </Box>
                                ))}

                                <Divider>
                                  <strong>По методам закупок</strong>
                                </Divider>
                                {yearItem.contracts?.byTradeMethod?.map(
                                  (method, i) => (
                                    <Box
                                      key={i}
                                      sx={{ ml: 2, cursor: "pointer" }}
                                      onClick={() =>
                                        loadContracts({
                                          year: yearItem.year,
                                          tradeMethodId: method.item?.code,
                                        })
                                      }
                                    >
                                      <Typography>
                                        {getValue(method.item?.name)} (
                                        {getValue(method.item?.code)})
                                      </Typography>
                                      <Typography variant="body2">
                                        Всего: {getValue(method.count)}, Сумма в
                                        году (без НДС):{" "}
                                        {getValue(method.totalAmount)}, Сумма в
                                        году (с НДС):{" "}
                                        {getValue(method.totalAmountWithNds)}
                                      </Typography>
                                    </Box>
                                  ),
                                )}
                              </Box>

                              {analysisMeta?.contractsStatus !==
                                ANALYSIS_STATUS.ANALYZED && (
                                <Typography
                                  sx={{ mt: 2 }}
                                  color="text.secondary"
                                >
                                  Список контрактов недоступен (статус:{" "}
                                  {STATUS_LABELS[
                                    analysisMeta?.contractsStatus
                                  ] || "неизвестно"}
                                  )
                                </Typography>
                              )}
                              {contractsByYear[yearItem.year]?.data?.length >
                                0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Divider>
                                    <strong>Список контрактов</strong>
                                  </Divider>
                                  {contractsByYear[yearItem.year].data.map(
                                    (contract, idx2) => (
                                      <Box
                                        key={idx2}
                                        sx={{
                                          border: "1px solid #ddd",
                                          borderRadius: "8px",
                                          p: 1,
                                          my: 1,
                                        }}
                                      >
                                        <Typography>
                                          <strong>ID договора:</strong>{" "}
                                          {contract.contractId}
                                        </Typography>
                                        <Typography>
                                          <strong>Дата подписания:</strong>{" "}
                                          {formatDate(
                                            getValue(contract.signDate),
                                          )}
                                        </Typography>
                                        <Typography>
                                          <strong>Заказчик:</strong>{" "}
                                          {contract.customer?.name ||
                                            "Не указано"}
                                        </Typography>
                                        <Typography>
                                          <strong>Описание:</strong>{" "}
                                          {getValue(contract.announce)}
                                        </Typography>
                                        <Typography>
                                          <strong>Сумма с НДС:</strong>{" "}
                                          {getValue(
                                            contract.totalAmountWithNds,
                                          )}
                                        </Typography>
                                        <Typography>
                                          <strong>Сумма без НДС:</strong>{" "}
                                          {getValue(contract.totalAmount)}
                                        </Typography>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </>
                    ) : (
                      <Typography color="text.secondary">
                        Сводка по контрактам недоступна
                        {analysisMeta &&
                          ` (статус: ${STATUS_LABELS[analysisMeta.contractsSummaryStatus] || "неизвестно"})`}
                      </Typography>
                    )}
                  </Box>

                  {/* --- Рейтинг КТРУ по контрактам ---
                      GET /api/companies/contracts/ktru-statistics.
                      Учитываются только контракты, где компания —
                      поставщик; contractsCount — уникальные контракты,
                      не позиции. */}
                  <Box>
                    <Divider sx={{ mb: 1 }}>
                      <strong>Рейтинг КТРУ по контрактам</strong>
                    </Divider>
                    {ktruContractsStats.loading &&
                    !ktruContractsStats.result ? (
                      <CircularProgress size={20} />
                    ) : ktruContractsStats.pending ? (
                      <Typography color="text.secondary">
                        Раздел ещё считается, данные появятся автоматически
                      </Typography>
                    ) : ktruContractsStats.unavailable ? (
                      <Box>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          Раздел анализа завершился с ошибкой
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={restarting}
                          onClick={handleRestartAnalysis}
                        >
                          {restarting ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Перезапустить анализ"
                          )}
                        </Button>
                      </Box>
                    ) : ktruContractsStats.error ? (
                      <Typography color="error">
                        {ktruContractsStats.error}
                      </Typography>
                    ) : ktruContractsStats.result ? (
                      <>
                        {ktruContractsStats.result.unmappedContractsCount >
                          0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Контрактов без указанного кода КТРУ ни на одной
                            позиции:{" "}
                            {ktruContractsStats.result.unmappedContractsCount}
                          </Typography>
                        )}
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          sx={{ boxShadow: "0 6px 16px rgba(0,0,0,0.10)" }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Код КТРУ</TableCell>
                                <TableCell>Название</TableCell>
                                <TableCell>Контрактов</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ktruContractsStats.result.data?.length ? (
                                ktruContractsStats.result.data.map(
                                  (row, idx) => (
                                    <TableRow key={`${row.ktruCode}-${idx}`}>
                                      <TableCell>{row.ktruCode}</TableCell>
                                      <TableCell>
                                        {row.ktruName || "—"}
                                      </TableCell>
                                      <TableCell>
                                        {row.contractsCount}
                                      </TableCell>
                                    </TableRow>
                                  ),
                                )
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    Нет данных
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <KtruPagination stats={ktruContractsStats} />
                      </>
                    ) : null}
                  </Box>

                  {/* --- налоги --- */}
                  <Box>
                    <Divider sx={{ mb: 1 }}>
                      <strong>Налоги</strong>
                    </Divider>
                    {taxes?.yearlySum?.length ? (
                      taxes.yearlySum.map((year, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            p: 1,
                            my: 1,
                          }}
                        >
                          <Typography>
                            <strong>Год: </strong> {getValue(year.key)}
                          </Typography>
                          <Typography>
                            <strong>Налог: </strong> {getValue(year.value)}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        {/* taxesStatus: 4 (HaventAnalyzed) — это норма, налоги почти никогда не приходят */}
                        Нет данных по налогам
                        {analysisMeta &&
                          analysisMeta.taxesStatus !==
                            ANALYSIS_STATUS.HAVENT_ANALYZED &&
                          ` (статус: ${STATUS_LABELS[analysisMeta.taxesStatus] || "неизвестно"})`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Collapse>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BinCompanyDetailsCard;
